import { Download, FileText } from "lucide-react";
import { type FormEvent, useState } from "react";
import { toast } from "sonner";
import { toolkitApi } from "@/api/toolkit";
import { Button, Card, Input } from "@/components/common";
import { TerminationPreview } from "@/components/studio/TerminationPreview";
import { useClients } from "@/hooks/useClients";
import {
  LAW_URL,
  localDate,
  type Reason,
  reasons,
  statement,
  type TerminationData,
  validate,
} from "./termination";

const initial: TerminationData = {
  reason: "art28",
  name: "",
  address: "",
  insurer: "",
  policy: "",
  brand: "",
  model: "",
  registration: "",
  start: "",
  end: "",
  city: "",
  date: localDate(),
  effectiveDate: "",
  acquisitionDate: "",
  otherInsurer: "",
  otherPolicy: "",
  coOwners: "",
};

export function TerminationTab({ studio = false }: { studio?: boolean }) {
  const [data, setData] = useState(initial);
  const [mode, setMode] = useState<"manual" | "client">("manual");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState("");
  const [dated, setDated] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  function update(key: keyof TerminationData, value: string) {
    setData((prev) => ({ ...prev, [key]: value }));
    setConfirmed(false);
    setError(null);
  }
  async function submit(event: FormEvent) {
    event.preventDefault();
    const issue = validate(data);
    if (issue) {
      setError(issue);
      return;
    }
    if (mode === "client" && !selected) {
      setError("Wybierz klienta z wyników albo przejdź do danych ręcznych.");
      return;
    }
    if (!confirmed) {
      setError("Potwierdź sprawdzenie danych i podstawy wypowiedzenia.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const { createTerminationPdf } = await import("./terminationPdf");
      const pdf = await createTerminationPdf(data);
      await toolkitApi.logTermination(data.reason, mode);
      pdf.save(
        `wypowiedzenie-oc-${data.reason}-${data.registration.replace(/[^a-zA-Z0-9]/g, "")}.pdf`,
      );
      toast.success("PDF gotowy. Pamiętaj o podpisie i przekazaniu do TU.");
    } catch {
      setError(
        "Nie udało się przygotować PDF lub zapisać zdarzenia w dzienniku. Spróbuj ponownie.",
      );
    } finally {
      setBusy(false);
    }
  }
  function field(
    key: keyof TerminationData,
    label: string,
    type = "text",
    optional = false,
  ) {
    return (
      <Input
        key={key}
        label={`${label}${optional ? " (opcjonalnie)" : " *"}`}
        type={type}
        required={!optional}
        maxLength={300}
        value={data[key]}
        onChange={(e) => update(key, e.target.value)}
      />
    );
  }
  const complete = !validate(data);
  return (
    <div className={studio ? "studio-document-workspace" : undefined}>
      <form
        onSubmit={submit}
        className={studio ? "space-y-4 studio-termination-form" : "space-y-5"}
      >
        <Card>
          <div className="flex items-start gap-3">
            <FileText
              className="mt-1 shrink-0 text-adara-700"
              size={24}
              aria-hidden
            />
            <div>
              <h2 className="text-lg font-semibold text-main">
                Generator wypowiedzenia OC
              </h2>
              <p className="text-sm text-dim">
                Wybierz sytuację, uzupełnij dane i pobierz dokument do podpisu.
                Dotyczy OC pojazdów mechanicznych.
              </p>
            </div>
          </div>
        </Card>
        <Card>
          <fieldset className="space-y-3">
            <legend className="mb-3 font-semibold text-main">
              1. Dlaczego klient wypowiada OC?
            </legend>
            <div className="grid gap-3 md:grid-cols-3">
              {Object.entries(reasons).map(([key, value]) => (
                <label
                  key={key}
                  className={`cursor-pointer rounded-lg border p-4 ${data.reason === key ? "border-adara-500 bg-muted" : "border-edge"}`}
                >
                  <span className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="reason"
                      value={key}
                      checked={data.reason === key}
                      onChange={() => {
                        update("reason", key as Reason);
                        update("effectiveDate", "");
                        setDated(false);
                      }}
                    />
                    <span className="text-sm font-semibold text-main">
                      {value.article}
                    </span>
                  </span>
                  <span className="mt-2 block text-sm text-main">
                    {value.title}
                  </span>
                </label>
              ))}
            </div>
            <p className="rounded-md bg-muted p-3 text-sm text-sub">
              {reasons[data.reason].help}
            </p>
            <a
              href={LAW_URL}
              target="_blank"
              rel="noreferrer"
              className="text-sm text-adara-700 underline"
            >
              Sprawdź podstawę w ustawie (Dz.U. 2026 poz. 783)
            </a>
          </fieldset>
        </Card>
        <Card>
          <fieldset className="space-y-4">
            <legend className="mb-3 font-semibold text-main">
              2. Dane właściciela / nabywcy
            </legend>
            <div className="flex flex-wrap gap-4">
              {(
                [
                  ["manual", "Wpisz ręcznie"],
                  ["client", "Wybierz z klientów RODO"],
                ] as const
              ).map(([value, label]) => (
                <label
                  key={value}
                  className="flex items-center gap-2 text-sm text-main"
                >
                  <input
                    type="radio"
                    name="clientMode"
                    checked={mode === value}
                    onChange={() => {
                      setMode(value);
                      setSelected("");
                      update("name", "");
                      update("address", "");
                    }}
                  />
                  {label}
                </label>
              ))}
            </div>
            {mode === "client" && (
              <ClientPicker
                search={search}
                onSearch={setSearch}
                selected={selected}
                onSelect={(id, name) => {
                  setSelected(id);
                  update("name", name);
                  update("address", "");
                }}
              />
            )}
            <div className="grid gap-4 sm:grid-cols-2">
              {field("name", "Imię i nazwisko / nazwa firmy")}
              {field("address", "Adres (ulica, numer, kod i miejscowość)")}
              {field("coOwners", "Pozostali współwłaściciele", "text", true)}
            </div>
            <p className="text-xs text-dim">
              Adres uzupełnij ręcznie. W przypadku współwłasności zadbaj o
              podpisy uprawnionych osób lub pełnomocnictwo.
            </p>
          </fieldset>
        </Card>
        <Card>
          <fieldset>
            <legend className="mb-3 font-semibold text-main">
              3. Pojazd i wypowiadana polisa
            </legend>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {field("brand", "Marka")}
              {field("model", "Model")}
              {field("registration", "Numer rejestracyjny")}
              {field("insurer", "Towarzystwo ubezpieczeń")}
              {field("policy", "Numer wypowiadanej polisy")}
              {field("start", "Początek ochrony", "date")}
              {field("end", "Koniec ochrony", "date")}
            </div>
          </fieldset>
        </Card>
        <Card>
          <fieldset className="space-y-4">
            <legend className="mb-3 font-semibold text-main">
              4. Daty i szczegóły wypowiedzenia
            </legend>
            <div className="grid gap-4 sm:grid-cols-2">
              {field("city", "Miejscowość sporządzenia")}
              {field("date", "Data sporządzenia", "date")}
              {data.reason === "art31" &&
                field("acquisitionDate", "Data nabycia pojazdu", "date")}
              {data.reason === "art28a" && (
                <>
                  {field("otherInsurer", "TU drugiej, równoległej umowy OC")}
                  {field("otherPolicy", "Numer drugiej polisy OC")}
                </>
              )}
            </div>
            {data.reason === "art31" && (
              <fieldset className="space-y-3">
                <legend className="mb-2 text-sm font-medium text-main">
                  Kiedy wypowiedzenie ma być skuteczne?
                </legend>
                <label className="flex items-center gap-2 text-sm text-main">
                  <input
                    type="radio"
                    name="effective"
                    checked={!dated}
                    onChange={() => {
                      setDated(false);
                      update("effectiveDate", "");
                    }}
                  />
                  Z dniem złożenia wypowiedzenia
                </label>
                <label className="flex items-center gap-2 text-sm text-main">
                  <input
                    type="radio"
                    name="effective"
                    checked={dated}
                    onChange={() => {
                      setDated(true);
                      update("effectiveDate", "");
                    }}
                  />
                  Z wybraną datą
                </label>
                {dated && (
                  <Input
                    label="Dzień rozwiązania umowy *"
                    type="date"
                    required
                    max={data.end || undefined}
                    value={data.effectiveDate}
                    onChange={(e) => update("effectiveDate", e.target.value)}
                  />
                )}
                <p className="text-sm text-dim">
                  Zgodnie z ustawą wybrany dzień nie może być wcześniejszy niż
                  faktyczny dzień złożenia oświadczenia. Sama data sporządzenia
                  nie oznacza złożenia wypowiedzenia.
                </p>
              </fieldset>
            )}
            {data.reason === "art28" && data.end && data.date >= data.end && (
              <p role="alert" className="text-sm text-red-600">
                Sprawdź termin: wypowiedzenie z art. 28 należy złożyć najpóźniej
                dzień przed końcem ochrony. Data dokumentu nie zastępuje daty
                złożenia.
              </p>
            )}
          </fieldset>
        </Card>
        <Card>
          <h3 className="mb-3 font-semibold text-main">5. Sprawdź i pobierz</h3>
          {complete ? (
            <p className="mb-4 rounded-lg bg-muted p-4 text-sm leading-relaxed text-main">
              {statement(data)}
            </p>
          ) : (
            <p className="mb-4 text-sm text-dim">
              Po uzupełnieniu wymaganych pól zobaczysz tutaj treść oświadczenia.
            </p>
          )}
          <label className="flex items-start gap-2 text-sm text-main">
            <input
              className="mt-1"
              type="checkbox"
              required
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
            />
            Sprawdziłem dane, właściwą podstawę prawną i termin. Dokument
            zostanie podpisany przez uprawnioną osobę i przekazany do TU;
            pobranie PDF nie wysyła wypowiedzenia.
          </label>
          {error && (
            <p role="alert" className="mt-3 text-sm text-red-600">
              {error}
            </p>
          )}
          <Button
            type="submit"
            className="mt-4"
            isLoading={busy}
            leftIcon={<Download size={16} aria-hidden />}
          >
            Pobierz PDF do podpisu / druku
          </Button>
        </Card>
      </form>
      {studio && <TerminationPreview data={data} />}
    </div>
  );
}

function ClientPicker({
  search,
  onSearch,
  selected,
  onSelect,
}: {
  search: string;
  onSearch: (value: string) => void;
  selected: string;
  onSelect: (id: string, name: string) => void;
}) {
  const { data, isLoading, isError, isFetching } = useClients({
    page: 1,
    pageSize: 20,
    search,
    rodo: "verified",
  });
  return (
    <div className="space-y-2">
      <Input
        label="Szukaj klienta z potwierdzonym RODO"
        value={search}
        onChange={(e) => onSearch(e.target.value)}
        placeholder="Wpisz imię lub nazwisko"
      />
      {isLoading && <p className="text-sm text-dim">Wczytywanie klientów…</p>}
      {isError && (
        <p role="alert" className="text-sm text-red-600">
          Nie udało się pobrać klientów. Spróbuj ponownie lub wpisz dane
          ręcznie.
        </p>
      )}
      {data && !isError && (
        <>
          <div className="max-h-48 overflow-y-auto rounded-md border border-edge">
            {data.items.map((client) => (
              <label
                key={client.id}
                className="flex cursor-pointer items-center gap-2 border-b border-edge-soft p-3 text-sm text-main"
              >
                <input
                  type="radio"
                  name="client"
                  disabled={isFetching}
                  checked={selected === client.id}
                  onChange={() =>
                    onSelect(
                      client.id,
                      `${client.firstName} ${client.lastName}`,
                    )
                  }
                />
                {client.firstName} {client.lastName}
                <span className="text-faint">
                  {client.peselMasked} · {client.agentName}
                </span>
              </label>
            ))}
            {data.items.length === 0 && (
              <p className="p-3 text-sm text-dim">
                Brak pasujących klientów z potwierdzonym RODO.
              </p>
            )}
          </div>
          {data.totalCount > 20 && (
            <p className="text-xs text-dim">
              Pokazano pierwsze 20 wyników. Zawęź wyszukiwanie nazwiskiem.
            </p>
          )}
        </>
      )}
    </div>
  );
}
